import { useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ExternalLink,
  HelpCircle,
  Hourglass,
  Loader2,
  Pause,
  X,
} from 'lucide-react'
import { type ComponentProps, useContext } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { Badge } from 'slab'
import { capitalize } from 'utils'
import {
  getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey,
  getGetBrokersTaskWorkflowsQueryKey,
  getGetBrokersUnprocessedEnrollmentsIdQueryKey,
} from 'utils/web_api'
import {
  type BrokersWorkflow,
  type BrokersWorkflowStatus,
  BrokersWorkflowStatus as BrokersWorkflowStatusEnum,
} from 'utils/web_api.schemas'

import WorkflowMetadataDetails from './WorkflowMetadataDetails'
import WorkflowProgressBar from './WorkflowProgressBar'
import AssignBrokersToWorkflow from './workflows/AssignBrokersToWorkflow'
import { useTaskActions } from './workflows/TaskActionControls'
import TasksList from './workflows/TasksList'
import WorkflowActionControls from './workflows/WorkflowActionControls'

type IconProp = ComponentProps<typeof Badge>['leftIcon']
type BadgeType = ComponentProps<typeof Badge>['type']

type WorkflowSummaryProps = {
  workflow: BrokersWorkflow
}

const getWorkflowBorderColor = (status: BrokersWorkflowStatus): string => {
  switch (status) {
    case BrokersWorkflowStatusEnum.pending:
      return 'border-l-4 border-slate-400 bg-slate-50' // Neutral/slate border - ready to start, no urgency
    case BrokersWorkflowStatusEnum.in_progress:
      return 'border-l-4 border-blue-400 bg-blue-50' // Info/blue border with subtle background
    case BrokersWorkflowStatusEnum.completed:
      return 'border-l-4 border-green-400 bg-green-50' // Success/green border with subtle background
    case BrokersWorkflowStatusEnum.canceled:
      return 'border-l-4 border-red-400 bg-red-50' // Critical/red border with subtle background
    case BrokersWorkflowStatusEnum.paused:
      return 'border-l-4 border-amber-400 bg-amber-50' // Warning/amber border - needs attention
    default:
      return 'border-l-4 border-gray-400 bg-gray-50' // Default/gray border with subtle background
  }
}

const getWorkflowBadgeMetadata = (
  status: BrokersWorkflowStatus
): { type: BadgeType; text: string; leftIcon: IconProp } => {
  // Create spinning loader component for in-progress status
  const SpinningLoader: IconProp = (props: any) => (
    <Loader2 className='animate-spin' {...props} />
  )

  switch (status) {
    case BrokersWorkflowStatusEnum.pending:
      return { type: 'slate', text: 'Pending', leftIcon: Hourglass }
    case BrokersWorkflowStatusEnum.in_progress:
      return { type: 'info', text: 'In Progress', leftIcon: SpinningLoader }
    case BrokersWorkflowStatusEnum.completed:
      return { type: 'success', text: 'Completed', leftIcon: Check }
    case BrokersWorkflowStatusEnum.canceled:
      return { type: 'critical', text: 'Canceled', leftIcon: X }
    case BrokersWorkflowStatusEnum.paused:
      return { type: 'warning', text: 'Paused', leftIcon: Pause }
    default:
      return { type: 'info', text: 'Unknown', leftIcon: HelpCircle }
  }
}

const WorkflowStatusBadge = ({ status }: { status: BrokersWorkflowStatus }) => {
  const { type, text, leftIcon } = getWorkflowBadgeMetadata(status)

  return (
    <Badge type={type} bordered leftIcon={leftIcon}>
      {text}
    </Badge>
  )
}

const WorkflowLinkBadge = ({ workflow }: { workflow: BrokersWorkflow }) => {
  return (
    <Badge
      type='info'
      bordered
      actionIcon={ExternalLink}
      onActionClick={() => {
        window.open(`/brokers/workflows/${workflow.id}`, '_blank')
      }}
    >
      View Workflow
    </Badge>
  )
}

const WorkflowSummary = ({ workflow }: WorkflowSummaryProps) => {
  const { memberId } = useContext(BrokersMemberContext)
  const queryClient = useQueryClient()

  const formatWorkflowType = (type: string) => {
    return type.split('_').map(capitalize).join(' ')
  }

  // Task actions hook with additional cache invalidation
  const { handleTaskAction, isLoading: isTaskLoading } = useTaskActions(
    workflow.id,
    {
      onSuccess: () => {
        // Also invalidate the workflow list query for this enrollment
        queryClient.invalidateQueries({
          queryKey: getGetBrokersTaskWorkflowsQueryKey({
            'filters[enrollment_ids][]': [workflow.insurance_enrollment_id],
            'sort[status]': 'asc',
          }),
        })
      },
    }
  )

  // Handler for additional cache invalidation specific to WorkflowSummary context
  const handleWorkflowActionSuccess = () => {
    // Invalidate the workflow list query for this enrollment
    queryClient.invalidateQueries({
      queryKey: getGetBrokersTaskWorkflowsQueryKey({
        'filters[enrollment_ids][]': [workflow.insurance_enrollment_id],
        'sort[status]': 'asc',
      }),
    })

    // Invalidate the unprocessed enrollment cache.
    // Technically we need to wait for the workflow to execute a few tasks before updating the cache, working on a
    // better solution for this but for now this is a workaround.
    // TODO: Remove this once we have a better solution.
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(
          workflow.insurance_enrollment_id
        ),
      })
      queryClient.invalidateQueries({
        queryKey:
          getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey(memberId),
      })
    }, 2000)
  }

  const completedTasks = workflow.completed_task_count || 0
  const totalTasks = workflow.task_count || 0
  const failedTasks = workflow.failed_task_count || 0

  return (
    <div
      className={`border rounded-lg p-4 space-y-3 transition-all duration-300 ${getWorkflowBorderColor(
        workflow.status
      )}`}
    >
      {/* Workflow Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-3'>
          <h4 className='font-semibold text-lg'>
            {formatWorkflowType(workflow.workflow_type)}
          </h4>
          <WorkflowStatusBadge status={workflow.status} />
          <WorkflowLinkBadge workflow={workflow} />
          <AssignBrokersToWorkflow
            id={workflow.id}
            assignedBrokers={workflow.brokers || []}
            enrollmentId={workflow.insurance_enrollment_id}
          />
        </div>

        <div className='flex items-center space-x-3'>
          <div className='text-sm text-gray-500'>
            {workflow.progress_percentage?.toFixed(0)}% Complete
          </div>

          {/* Action Controls */}
          <WorkflowActionControls
            workflow={workflow}
            onSuccess={handleWorkflowActionSuccess}
          />
        </div>
      </div>

      {/* Progress Bar */}
      {workflow.tasks_summary && workflow.tasks_summary.length > 0 && (
        <WorkflowProgressBar tasks={workflow.tasks_summary} />
      )}

      {/* Progress Summary */}
      <div className='flex items-center space-x-4 text-sm'>
        <span className='text-gray-600'>
          {completedTasks}/{totalTasks} tasks completed
        </span>
        {failedTasks > 0 && (
          <span className='text-red-600'>{failedTasks} failed</span>
        )}
        {workflow.started_at && (
          <span className='text-gray-500'>
            Started {new Date(workflow.started_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Workflow Details */}
      <WorkflowMetadataDetails workflow={workflow} headerVariant='compact' />

      {/* Tasks List */}
      {workflow.tasks_summary && workflow.tasks_summary.length > 0 && (
        <TasksList
          tasks={workflow.tasks_summary}
          workflowStatus={workflow.status}
          workflowId={workflow.id}
          enrollmentId={workflow.insurance_enrollment_id}
          handleTaskAction={handleTaskAction}
          taskMutation={{ isPending: isTaskLoading }}
          showActions
        />
      )}
    </div>
  )
}

export default WorkflowSummary
